import type { MouseEvent } from 'react';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const BASE_MARKDOWN_LINK_BUTTON_STYLE = [
  'width:100%',
  'padding:8px 12px',
  'background-color:transparent',
  'cursor:pointer',
  'text-align:center',
  'font-size:inherit',
  'font-family:inherit',
  'margin:7px 0',
  'border-radius:4px',
  'box-sizing:border-box',
  'display:block',
] as const;

const MARKDOWN_LINK_BUTTON_VARIANTS = {
  default: {
    color: '#000',
    border: '1px solid gray',
  },
  chatbox: {
    color: '#fff',
    border: '1px solid white',
  },
} as const;

type MarkdownButtonVariant = keyof typeof MARKDOWN_LINK_BUTTON_VARIANTS;

const getMarkdownLinkButtonStyle = (variant: MarkdownButtonVariant = 'default'): string => {
  const { color, border } = MARKDOWN_LINK_BUTTON_VARIANTS[variant];
  return [...BASE_MARKDOWN_LINK_BUTTON_STYLE, `color:${color}`, `border:${border}`].join(';');
};

export const parseMarkdown = (text: string, variant: MarkdownButtonVariant = 'default'): string => {
  if (!text) return text;

  const buttonStyle = getMarkdownLinkButtonStyle(variant);

  // Convert **text** to <b>text</b>
  text = text.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');

  // Convert [content link](address link) to button
  text = text.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_match, label: string, href: string) =>
      `<button type="button" class="markdown-link-button" data-href="${escapeHtml(href)}" style="${buttonStyle}">${escapeHtml(label)}</button>`
  );

  // Convert bare http(s) URLs to links (skip segments already inside HTML tags)
  text = text
    .split(/(<[^>]+>)/g)
    .map((segment) => {
      if (segment.startsWith('<')) return segment;
      return segment.replace(
        /(https?:\/\/[^\s<]+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
      );
    })
    .join('');

  return text;
};

export const handleMarkdownButtonClick = (event: MouseEvent<HTMLElement>) => {
  const button = (event.target as HTMLElement).closest('.markdown-link-button') as HTMLButtonElement | null;
  if (!button) return;

  event.preventDefault();
  const href = button.dataset.href;
  if (href) {
    window.open(href, '_blank', 'noopener,noreferrer');
  }
};
